/**
 * The sign-in pane, on the screen that asked for it.
 *
 * Signing an account in opens `claude auth login` in a terminal on the account's
 * own host, and the operator has to reach that terminal: the authorisation code
 * can only be pasted into it. That requirement used to be met by *navigating* —
 * Hesaplar handed the operator to /live and appended the pane to whatever wall
 * they already had open, which is what "it redirects me from the account page
 * back to the sessions page" was describing. It also left the account they were
 * working on behind, so the half of the job that happens afterwards — confirming
 * the credential landed — was on a screen they were no longer looking at.
 *
 * The terminal comes to them instead. TerminalPane is built to mount on its own
 * (it draws no seat and answers no chord outside a wall), so the pane, the
 * account's name and the confirmation all sit in one dialog, and closing it puts
 * the operator back exactly where they were.
 *
 * The pane is left running when the dialog closes. It is the operator's sign-in,
 * not the dialog's: they may want to finish it in the full terminal, and the
 * server reuses a live pane for fifteen minutes, so reopening returns the same
 * one rather than a second.
 */
import { useNavigate } from 'react-router-dom'

import { Dialog, Notice } from '@/components/controls'
import { Button } from '@/components/primitives'
import { useT } from '@/i18n/context'
import { TerminalPane } from '@/terminal/TerminalPane'
import { useSessionDetail, sessionLabel } from '@/terminal/wall'
import { sessionHasEnded } from '@/lib/sessionActivity'
import { SignInConfirm } from './signInConfirm'

export function SignInDialog({
  sessionId,
  accountName,
  onClose,
}: {
  sessionId: string
  accountName: string
  onClose: () => void
}) {
  const t = useT()
  const navigate = useNavigate()
  const detail = useSessionDetail(sessionId)
  const d = detail.data
  const ended = d ? sessionHasEnded(d.state) : false

  return (
    <Dialog
      title={t('account.signInPaneTitle', { name: accountName })}
      onClose={onClose}
      wide
      footer={
        <>
          {/* The way out to the full terminal, for an operator who wants the
              wall's room. Offered rather than imposed: they choose it. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onClose()
              navigate(`/live/${sessionId}`)
            }}
          >
            {t('account.signInOpenInTerminal')}
          </Button>
          <Button variant="primary" size="sm" onClick={onClose}>
            {t('action.close')}
          </Button>
        </>
      }
    >
      <p className="k-signin__hint">{t('account.signInPaneHint')}</p>
      <div className="k-signin__pane">
        {d && !d.terminalAvailable ? (
          // The gateway is not built into this deployment, so mounting the pane
          // would draw a black rectangle that reconnects for ever and never says
          // why. The wall states this in the same words.
          <Notice tone="warn">{t('terminal.gatewayUnavailable')}</Notice>
        ) : (
          <TerminalPane
            sessionId={sessionId}
            mode="CONTROL"
            kind={d?.kind}
            ended={ended}
            title={sessionLabel(d, sessionId)}
          />
        )}
      </div>
      {/* Confirms the credential landed as soon as the pane ends — the step the
          operator used to have to go and find. */}
      <SignInConfirm session={d} />
    </Dialog>
  )
}
